import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import { auth, db } from "../Config/firebase";
import { aplicarTenantAtual, consultaTenantAtual } from "../Utils/tenant";
import RelatorioProgramaTreinamentosPrint from "./RelatorioProgramaTreinamentosPrint";

const COLLECTION = "tblProgramasTreinamentos";

const CONTEUDO_INTEGRACAO = "Política da Qualidade e Código de Conduta\nProcedimentos técnicos e de segurança\nResponsabilidades relacionadas ao cargo\nNormas e POP's aplicados ao cargo\nAvaliação de desempenho";
const CONTEUDO_NR = "Ver norma regulamentadora no site do MTE - sempre atualizar no verso do certificado";

const TREINAMENTOS_PADRAO = [
  {
    treinamentoPlanejado: "Integração (8h)",
    conteudoProgramatico: CONTEUDO_INTEGRACAO,
    publicoAlvo: "Todos os novos colaboradores",
    frequenciaPrazo: "Sempre que necessário",
    instrutor: "Encarregado do laboratório",
    mesPrevisto: "Conforme admissão",
    status: "Realizado",
  },
  {
    treinamentoPlanejado: "NR 6 - Equipamentos de proteção individual (4h)",
    conteudoProgramatico: CONTEUDO_NR,
    publicoAlvo: "Todos os colaboradores envolvidos no processo operacional",
    frequenciaPrazo: "Anual",
    instrutor: "RT seg. do trabalho",
    mesPrevisto: "Conforme admissão",
    status: "Realizado",
  },
  {
    treinamentoPlanejado: "NR 12 - Segurança com máquinas e equipamentos (8h)",
    conteudoProgramatico: CONTEUDO_NR,
    publicoAlvo: "Todos os colaboradores envolvidos no processo operacional",
    frequenciaPrazo: "Anual",
    instrutor: "RT seg. do trabalho",
    mesPrevisto: "Conforme admissão",
    status: "Realizado",
  },
  {
    treinamentoPlanejado: "NR 18 - Segurança e Saúde no Trabalho na Indústria da Construção (4h)",
    conteudoProgramatico: CONTEUDO_NR,
    publicoAlvo: "Todos os colaboradores envolvidos no processo operacional",
    frequenciaPrazo: "Anual",
    instrutor: "RT seg. do trabalho",
    mesPrevisto: "Conforme admissão",
    status: "Realizado",
  },
];

const treinamentoVazio = () => ({
  treinamentoPlanejado: "",
  conteudoProgramatico: "",
  publicoAlvo: "",
  frequenciaPrazo: "",
  instrutor: "",
  mesPrevisto: "",
  status: "Planejado",
});

function criarProgramaInicial() {
  return {
    versao: "01",
    aprovadoEm: "2025-09-20",
    treinamentos: TREINAMENTOS_PADRAO.map((item) => ({ ...item })),
  };
}

function formatarData(data) {
  if (!data) return "-";
  const [ano, mes, dia] = String(data).split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : data;
}

function corStatus(status) {
  if (status === "Realizado") return "#078568";
  if (status === "Em andamento") return "#ed6c02";
  if (status === "Cancelado") return "#d32f2f";
  return "#1976d2";
}

function ProgramaVisual({ item, onEditar, onAdicionarItem, onRelatorio }) {
  const treinamentos = Array.isArray(item.treinamentos) ? item.treinamentos : [];
  const cabecalho = { backgroundColor: "#9dc3e6", color: "#082f5b", border: "1px solid #6aa0d8", fontWeight: 800 };
  const celula = { border: "1px solid #6aa0d8", whiteSpace: "pre-line" };

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="body2" fontWeight={700}>
          RQ 5.9 — Versão {item.versao || "-"} — Aprovado em {formatarData(item.aprovadoEm)}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => onEditar(item)}>Editar programa</Button>
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => onAdicionarItem(item)}>Novo treinamento</Button>
          <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => onRelatorio(item)}>Relatório</Button>
          <HistoricoCadastro colecao={COLLECTION} documentoId={item.id} registro={item} />
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 1250, tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ ...cabecalho, width: "18%" }}>Treinamento Planejado</TableCell>
              <TableCell align="center" sx={{ ...cabecalho, width: "27%" }}>Conteúdo programático</TableCell>
              <TableCell align="center" sx={{ ...cabecalho, width: "17%" }}>Público-Alvo</TableCell>
              <TableCell align="center" sx={{ ...cabecalho, width: "11%" }}>Frequência/Prazo</TableCell>
              <TableCell align="center" sx={{ ...cabecalho, width: "10%" }}>Instrutor</TableCell>
              <TableCell align="center" sx={{ ...cabecalho, width: "10%" }}>Mês Previsto</TableCell>
              <TableCell align="center" sx={{ ...cabecalho, width: "7%" }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {treinamentos.map((treinamento, indice) => (
              <TableRow key={`${treinamento.treinamentoPlanejado}-${indice}`}>
                <TableCell sx={{ ...celula, fontWeight: 700, color: "#267326" }}>{treinamento.treinamentoPlanejado || "-"}</TableCell>
                <TableCell sx={celula}>{treinamento.conteudoProgramatico || "-"}</TableCell>
                <TableCell sx={celula}>{treinamento.publicoAlvo || "-"}</TableCell>
                <TableCell sx={celula}>{treinamento.frequenciaPrazo || "-"}</TableCell>
                <TableCell sx={celula}>{treinamento.instrutor || "-"}</TableCell>
                <TableCell sx={celula}>{treinamento.mesPrevisto || "-"}</TableCell>
                <TableCell align="center" sx={celula}>
                  <Box sx={{ px: 1, py: 0.3, borderRadius: 5, color: "white", bgcolor: corStatus(treinamento.status), fontWeight: 700, fontSize: "0.72rem" }}>
                    {treinamento.status || "Planejado"}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

export default function CadastroProgramaTreinamentos() {
  const [programas, setProgramas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [dialogAberto, setDialogAberto] = useState(false);
  const [programaEditando, setProgramaEditando] = useState(null);
  const [programaRelatorio, setProgramaRelatorio] = useState(null);
  const [programa, setPrograma] = useState(criarProgramaInicial());
  const [notificacao, setNotificacao] = useState({ aberta: false, mensagem: "", tipo: "success" });
  const cadastrandoPadraoRef = useRef(false);

  const programasExibidos = useMemo(() => {
    const chaves = new Set();
    return programas.filter((item) => {
      const chave = `${item.versao || ""}|${item.aprovadoEm || ""}`;
      if (chaves.has(chave)) return false;
      chaves.add(chave);
      return true;
    });
  }, [programas]);

  async function carregarProgramas() {
    setCarregando(true);
    try {
      const snapshot = await getDocs(consultaTenantAtual(query(collection(db, COLLECTION), orderBy("criadoEm", "desc"))));
      setProgramas(snapshot.docs.map((documento) => ({ id: documento.id, ...documento.data() })));
    } catch (error) {
      console.error("Erro ao carregar Programas de Treinamentos:", error);
      setNotificacao({ aberta: true, mensagem: "Não foi possível carregar os Programas de Treinamentos.", tipo: "error" });
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregarProgramas();
  }, []);

  function dadosParaEdicao(item, adicionarItem = false) {
    return {
      versao: item.versao || "01",
      aprovadoEm: item.aprovadoEm || "",
      treinamentos: [
        ...(Array.isArray(item.treinamentos) ? item.treinamentos.map((treinamento) => ({ ...treinamento })) : []),
        ...(adicionarItem ? [treinamentoVazio()] : []),
      ],
    };
  }

  function abrirEdicao(item) {
    setProgramaEditando(item);
    setPrograma(dadosParaEdicao(item));
    setDialogAberto(true);
  }

  function abrirNovoTreinamento(item) {
    setProgramaEditando(item);
    setPrograma(dadosParaEdicao(item, true));
    setDialogAberto(true);
  }

  async function cadastrarProgramaPadrao() {
    if (programas.length > 0 || cadastrandoPadraoRef.current) return;
    cadastrandoPadraoRef.current = true;
    setSalvando(true);
    const programaInicial = criarProgramaInicial();
    try {
      const documentoCriado = await addDoc(collection(db, COLLECTION), aplicarTenantAtual({
        ...programaInicial,
        criadoEm: serverTimestamp(),
        criadoPor: auth.currentUser?.uid || null,
        atualizadoEm: serverTimestamp(),
        atualizadoPor: auth.currentUser?.uid || null,
      }));
      setProgramas([{ id: documentoCriado.id, ...programaInicial }]);
      setNotificacao({ aberta: true, mensagem: "Programa padrão versão 01 cadastrado com sucesso.", tipo: "success" });
    } catch (error) {
      console.error("Erro ao cadastrar Programa de Treinamentos padrão:", error);
      setNotificacao({ aberta: true, mensagem: "Não foi possível cadastrar o programa padrão.", tipo: "error" });
    } finally {
      cadastrandoPadraoRef.current = false;
      setSalvando(false);
    }
  }

  function atualizarPrograma(campo, valor) {
    setPrograma((atual) => ({ ...atual, [campo]: valor }));
  }

  function atualizarTreinamento(indice, campo, valor) {
    setPrograma((atual) => ({
      ...atual,
      treinamentos: atual.treinamentos.map((item, index) => index === indice ? { ...item, [campo]: valor } : item),
    }));
  }

  function adicionarTreinamento() {
    setPrograma((atual) => ({ ...atual, treinamentos: [...atual.treinamentos, treinamentoVazio()] }));
  }

  function removerTreinamento(indice) {
    setPrograma((atual) => ({ ...atual, treinamentos: atual.treinamentos.filter((_, index) => index !== indice) }));
  }

  async function salvar() {
    if (!programa.versao.trim()) {
      setNotificacao({ aberta: true, mensagem: "Informe a versão do programa.", tipo: "warning" });
      return;
    }
    if (programa.treinamentos.some((item) => !item.treinamentoPlanejado.trim())) {
      setNotificacao({ aberta: true, mensagem: "Informe o treinamento planejado em todas as linhas.", tipo: "warning" });
      return;
    }

    setSalvando(true);
    try {
      await updateDoc(doc(db, COLLECTION, programaEditando.id), {
        versao: programa.versao.trim(),
        aprovadoEm: programa.aprovadoEm,
        treinamentos: programa.treinamentos.map((item) => ({
          treinamentoPlanejado: item.treinamentoPlanejado.trim(),
          conteudoProgramatico: item.conteudoProgramatico.trim(),
          publicoAlvo: item.publicoAlvo.trim(),
          frequenciaPrazo: item.frequenciaPrazo.trim(),
          instrutor: item.instrutor.trim(),
          mesPrevisto: item.mesPrevisto.trim(),
          status: item.status || "Planejado",
        })),
        atualizadoEm: serverTimestamp(),
        atualizadoPor: auth.currentUser?.uid || null,
      });
      setDialogAberto(false);
      setNotificacao({ aberta: true, mensagem: "Programa de Treinamentos atualizado com sucesso.", tipo: "success" });
      await carregarProgramas();
    } catch (error) {
      console.error("Erro ao salvar Programa de Treinamentos:", error);
      setNotificacao({ aberta: true, mensagem: "Não foi possível salvar o Programa de Treinamentos.", tipo: "error" });
    } finally {
      setSalvando(false);
    }
  }

  if (programaRelatorio) {
    return <RelatorioProgramaTreinamentosPrint programa={programaRelatorio} onVoltar={() => setProgramaRelatorio(null)} />;
  }

  return (
    <>
      <Navbar />
      <Box sx={{ p: { xs: 2, md: 4 }, mt: 6 }}>
        <Paper className="cabecalho-lista-cadastro" elevation={0} sx={{ mb: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} alignItems={{ md: "center" }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>RQ 5.9 — Programa de Treinamentos</Typography>
              <Typography variant="body2" color="text.secondary">Planejamento dos treinamentos do Sistema de Gestão da Qualidade.</Typography>
            </Box>
            {programas.length === 0 && !carregando && <Button variant="outlined" onClick={cadastrarProgramaPadrao} disabled={salvando}>Cadastrar programa padrão</Button>}
          </Stack>

          {carregando && <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>}
          {!carregando && programas.length === 0 && <Box sx={{ p: 4, textAlign: "center" }}>Nenhum Programa de Treinamentos cadastrado.</Box>}
          {!carregando && programasExibidos.map((item) => <ProgramaVisual key={item.id} item={item} onEditar={abrirEdicao} onAdicionarItem={abrirNovoTreinamento} onRelatorio={setProgramaRelatorio} />)}
        </Paper>
      </Box>

      <Dialog open={dialogAberto} onClose={() => !salvando && setDialogAberto(false)} fullWidth maxWidth="xl">
        <DialogTitle>Editar Programa de Treinamentos</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField label="Versão" value={programa.versao} onChange={(event) => atualizarPrograma("versao", event.target.value)} sx={{ maxWidth: { sm: 180 } }} fullWidth required />
              <TextField label="Aprovado em" type="date" value={programa.aprovadoEm} onChange={(event) => atualizarPrograma("aprovadoEm", event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            </Stack>

            <Typography variant="h6" fontWeight={700}>Programa de treinamentos</Typography>
            <Paper variant="outlined" sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 1500 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Treinamento planejado</TableCell>
                    <TableCell>Conteúdo programático</TableCell>
                    <TableCell>Público-alvo</TableCell>
                    <TableCell>Frequência/Prazo</TableCell>
                    <TableCell>Instrutor</TableCell>
                    <TableCell>Mês previsto</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {programa.treinamentos.map((item, indice) => (
                    <TableRow key={indice}>
                      <TableCell sx={{ minWidth: 230 }}><TextField value={item.treinamentoPlanejado} onChange={(event) => atualizarTreinamento(indice, "treinamentoPlanejado", event.target.value)} size="small" multiline minRows={2} fullWidth required /></TableCell>
                      <TableCell sx={{ minWidth: 320 }}><TextField value={item.conteudoProgramatico} onChange={(event) => atualizarTreinamento(indice, "conteudoProgramatico", event.target.value)} size="small" multiline minRows={4} fullWidth /></TableCell>
                      <TableCell sx={{ minWidth: 230 }}><TextField value={item.publicoAlvo} onChange={(event) => atualizarTreinamento(indice, "publicoAlvo", event.target.value)} size="small" multiline minRows={3} fullWidth /></TableCell>
                      <TableCell sx={{ minWidth: 170 }}><TextField value={item.frequenciaPrazo} onChange={(event) => atualizarTreinamento(indice, "frequenciaPrazo", event.target.value)} size="small" multiline minRows={2} fullWidth /></TableCell>
                      <TableCell sx={{ minWidth: 170 }}><TextField value={item.instrutor} onChange={(event) => atualizarTreinamento(indice, "instrutor", event.target.value)} size="small" multiline minRows={2} fullWidth /></TableCell>
                      <TableCell sx={{ minWidth: 170 }}><TextField value={item.mesPrevisto} onChange={(event) => atualizarTreinamento(indice, "mesPrevisto", event.target.value)} size="small" multiline minRows={2} fullWidth /></TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        <TextField select value={item.status || "Planejado"} onChange={(event) => atualizarTreinamento(indice, "status", event.target.value)} size="small" fullWidth>
                          <MenuItem value="Planejado">Planejado</MenuItem>
                          <MenuItem value="Em andamento">Em andamento</MenuItem>
                          <MenuItem value="Realizado">Realizado</MenuItem>
                          <MenuItem value="Cancelado">Cancelado</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell><Tooltip title="Remover treinamento"><IconButton size="small" color="error" onClick={() => removerTreinamento(indice)}><DeleteOutlineIcon /></IconButton></Tooltip></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
            <Button startIcon={<AddIcon />} onClick={adicionarTreinamento} sx={{ alignSelf: "flex-start" }}>Adicionar treinamento</Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogAberto(false)} disabled={salvando}>Cancelar</Button>
          <Button variant="contained" onClick={salvar} disabled={salvando}>{salvando ? "Salvando..." : "Salvar"}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={notificacao.aberta} autoHideDuration={5000} onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}>
        <Alert severity={notificacao.tipo} onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}>{notificacao.mensagem}</Alert>
      </Snackbar>
    </>
  );
}
